/*
 * Selective quick add for product cards.
 *
 * Liquid only renders this control for an available, single-variant Sealed or
 * Supplies product. The ordinary product form is the no-JavaScript fallback;
 * enhancement keeps both success and Shopify errors on the current page.
 */
if ('customElements' in window && !customElements.get('product-card-buy')) {
  customElements.define(
    'product-card-buy',
    class ProductCardBuy extends HTMLElement {
      connectedCallback() {
        if (this.events) return;
        this.form = this.querySelector('[data-card-buy-form]');
        this.addView = this.querySelector('[data-card-buy-add]');
        this.button = this.querySelector('[data-card-buy-submit]');
        this.label = this.querySelector('[data-card-buy-label]');
        this.inCartLink = this.querySelector('[data-card-buy-in-cart]');
        this.inCartLabel = this.querySelector('[data-card-buy-in-cart-label]');
        this.feedback = this.querySelector('[data-card-buy-feedback]');
        if (
          !this.form ||
          !this.addView ||
          !this.button ||
          !this.label ||
          !this.inCartLink ||
          !this.inCartLabel ||
          !this.feedback ||
          !this.dataset.cartAddUrl
        ) return;

        this.defaultLabel = this.label.textContent;
        this.cartQuantity = Number(this.dataset.cartQuantity) || 0;
        this.events = new AbortController();
        this.form.addEventListener('submit', (event) => this.submitAdd(event), {
          signal: this.events.signal,
        });
        this.dataset.enhanced = '';
      }

      disconnectedCallback() {
        this.events?.abort();
        this.events = null;
        this.request?.abort();
        window.clearTimeout(this.celebrationTimer);
      }

      async submitAdd(event) {
        event.preventDefault();
        if (this.pending) return;
        const restoreFocus = document.activeElement === this.button;

        this.pending = true;
        this.request = new AbortController();
        window.clearTimeout(this.celebrationTimer);
        this.clearFeedback();
        this.button.disabled = true;
        this.button.setAttribute('aria-busy', 'true');
        this.label.textContent = this.dataset.addingCopy || this.defaultLabel;

        try {
          const response = await fetch(this.dataset.cartAddUrl, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: new FormData(this.form),
            signal: this.request.signal,
          });
          const payload = await this.readPayload(response);
          if (!response.ok) throw new Error(this.messageFrom(payload));

          this.showAdded();

          try {
            this.syncCart(await this.readCart(), true);
          } catch {
            // The item was added. A temporarily stale badge is not an add failure.
          }
        } catch (error) {
          if (error.name === 'AbortError') return;
          try {
            // Shopify can partially accept an add before returning 422.
            this.syncCart(await this.readCart());
          } catch {
            // Keep the native form usable if the cart endpoint is offline too.
          }
          this.showFeedback(error.message || this.dataset.errorCopy || '', true);
          this.label.textContent = this.defaultLabel;
        } finally {
          this.request = null;
          this.pending = false;
          this.label.textContent = this.defaultLabel;
          this.button.disabled = false;
          this.button.removeAttribute('aria-busy');
          if (restoreFocus && [document.body, this.button].includes(document.activeElement)) {
            (this.inCartLink.hidden ? this.button : this.inCartLink).focus({ preventScroll: true });
          }
        }
      }

      async readPayload(response) {
        const text = await response.text();
        if (!text) return {};
        try {
          return JSON.parse(text);
        } catch {
          // Proxy/challenge HTML is not shopper-facing error copy.
          return {};
        }
      }

      messageFrom(payload) {
        const message = payload?.description || payload?.message;
        return typeof message === 'string' && message.trim()
          ? message.trim()
          : this.dataset.errorCopy || '';
      }

      async readCart() {
        const response = await fetch(`${this.dataset.cartUrl}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      }

      quantityFrom(cart) {
        if (!Array.isArray(cart?.items)) return null;
        return cart.items.reduce((quantity, item) => {
          const variantId = item.variant_id ?? item.variant?.id;
          return String(variantId) === String(this.dataset.variantId)
            ? quantity + (Number(item.quantity) || 0)
            : quantity;
        }, 0);
      }

      renderCartQuantity(quantity, preserveCelebration = false) {
        const cartLinkFocused = document.activeElement === this.inCartLink;
        this.cartQuantity = Math.max(0, Number(quantity) || 0);
        this.dataset.cartQuantity = String(this.cartQuantity);
        this.addView.hidden = this.cartQuantity > 0;
        this.inCartLink.hidden = this.cartQuantity === 0;
        if (cartLinkFocused && this.cartQuantity === 0) this.button.focus({ preventScroll: true });

        if (preserveCelebration && this.dataset.state === 'just-added') return;
        delete this.dataset.state;
        this.inCartLabel.textContent = this.cartQuantity > 1
          ? (this.dataset.inCartCountCopy || '__COUNT__ in cart').replace('__COUNT__', String(this.cartQuantity))
          : this.dataset.inCartCopy || 'In cart';
      }

      showAdded() {
        const added = this.dataset.addedCopy || this.defaultLabel;
        const badge = document.querySelector('[data-cart-count]');
        this.syncHeader({ item_count: (Number(badge?.textContent) || 0) + 1 }, true);
        this.renderCartQuantity(Math.max(1, this.cartQuantity + 1));
        this.inCartLabel.textContent = added;
        this.dataset.state = 'just-added';
        this.showFeedback(added);

        this.celebrationTimer = window.setTimeout(() => {
          this.renderCartQuantity(this.cartQuantity);
        }, 1800);
      }

      syncCart(cart, celebrate = false) {
        this.syncHeader(cart);
        if (!Array.isArray(cart?.items)) return;

        document.querySelectorAll('product-card-buy').forEach((card) => {
          if (typeof card.quantityFrom !== 'function' || typeof card.renderCartQuantity !== 'function') return;
          const quantity = card.quantityFrom(cart);
          card.renderCartQuantity(quantity, card === this && celebrate);
        });
      }

      syncHeader(cart, celebrate = false) {
        const count = Number(cart?.item_count) || 0;
        document.querySelectorAll('[data-cart-link]').forEach((link) => {
          const badge = link.querySelector('[data-cart-count]');
          if (badge) {
            badge.textContent = String(count);
            badge.hidden = count === 0;
          }

          if (count === 0) link.setAttribute('aria-label', link.dataset.cartEmptyLabel || '');
          else if (count === 1) link.setAttribute('aria-label', link.dataset.cartOneLabel || '');
          else {
            link.setAttribute(
              'aria-label',
              (link.dataset.cartOtherLabel || '').replace('__COUNT__', String(count))
            );
          }

          if (celebrate) {
            link.dataset.state = 'just-added';
            window.setTimeout(() => delete link.dataset.state, 650);
          }
        });
      }

      clearFeedback() {
        this.feedback.hidden = true;
        this.feedback.textContent = '';
        delete this.feedback.dataset.state;
      }

      showFeedback(message, error = false) {
        this.feedback.textContent = message;
        this.feedback.hidden = false;
        if (error) this.feedback.dataset.state = 'error';
        else delete this.feedback.dataset.state;
      }
    }
  );
}
